/**
 * SFX trace — a dev-only record of every cue that PLAYED and every cue that was
 * BLOCKED, with the gate that blocked it.
 *
 * Built for a specific problem: a cue that does not sound gives you nothing to
 * look at. The fold cue crosses tables correctly and the win cue does not, and
 * reading the code has not explained why, because the interesting part is which
 * of six gates said no on a table nobody was watching.
 *
 * Off unless `localStorage.hijack_sfxlab` is set, and the recorder is a no-op
 * when off, so this costs nothing in a normal session.
 */

export type SfxEvent = {
  at: number;
  src: string;
  /** null when it played. */
  blockedBy: string | null;
  gameID: string;
  crossTable: boolean;
  isActiveTable: boolean;
};

const MAX = 300;
const events: SfxEvent[] = [];
const listeners = new Set<() => void>();

export const SFX_LAB_FLAG_KEY = 'hijack_sfxlab';

export const isSfxTraceOn = (): boolean => {
  try {
    return localStorage.getItem(SFX_LAB_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * URL switch, so nobody has to open DevTools to reach the lab — or to get rid
 * of it (owner, 2026-09-11, testing the lobby on test1: the panel followed him
 * around a lower environment because the flag had been set in that browser
 * weeks earlier, and the only way out was a console command he had no reason
 * to know).
 *
 *   ?sfxlab=1 | on    turn the lab on (sticks in this browser, like the flag)
 *   ?sfxlab=0 | off   turn it off and forget it
 *   anything else     no-op — the parameter is stripped and nothing changes
 *
 * Read once at import; the parameter is then removed from the address bar so a
 * plain reload does not re-apply it. Mirrors the Lobby Lab's switch
 * (components/LobbyLab/lobbyLabState.ts) — one habit for both labs.
 *
 * Deliberately NOT gated on the environment allowlist: `?sfxlab=1` still can't
 * show the lab where LAB_AVAILABLE is false (components/SfxLab/index.tsx keeps
 * that lock), and turning the lab OFF must work everywhere, unconditionally.
 */
const adoptSfxLabQuery = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const value = url.searchParams.get('sfxlab');
    if (value === null) return;
    const wanted = value.toLowerCase();
    // ONLY the documented values act. A bare `?sfxlab=`, a typo, or
    // `?sfxlab=false` used to fall through to the enable branch and write the
    // flag, which is the opposite of what any of them mean and left the panel
    // on in that browser for good (review, 2026-09-12). Unknown values are a
    // no-op; the parameter is still stripped below either way.
    if (wanted === 'off' || wanted === '0') localStorage.removeItem(SFX_LAB_FLAG_KEY);
    else if (wanted === 'on' || wanted === '1') localStorage.setItem(SFX_LAB_FLAG_KEY, '1');
    url.searchParams.delete('sfxlab');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* noop — the lab is a convenience, never a dependency */
  }
};

adoptSfxLabQuery();

export const recordSfx = (e: Omit<SfxEvent, 'at'>): void => {
  if (!isSfxTraceOn()) return;
  events.push({ ...e, at: Date.now() });
  if (events.length > MAX) events.splice(0, events.length - MAX);
  listeners.forEach((l) => l());
};

export const getSfxEvents = (): SfxEvent[] => events;
export const clearSfxEvents = (): void => {
  events.length = 0;
  listeners.forEach((l) => l());
};
export const subscribeSfx = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** Last path segment, minus the content hash webpack adds. */
export const shortSrc = (src: string | string[]): string => {
  const one = Array.isArray(src) ? src[0] ?? '' : src ?? '';
  const file = String(one).split('/').pop() ?? '';
  return file.replace(/\.[a-f0-9]{8,}\./, '.');
};
